import type { ApiEnvelope } from '~/types/types'

export interface BookingProfile {
  id: number
  user_id: number
  display_name: string
  slug: string
  avatar: string | null
  title: string | null
  bio: string | null
  expertise: string[]
  main_price: number
  currency: string | null
  session_duration: number
  is_bookable: boolean
  is_published: boolean
  timezone: string
  sessions_count: number
}

export interface BookingProfileInput {
  title?: string | null
  bio?: string | null
  expertise?: string[] | null
  main_price?: number | null
  session_duration?: number | null
  currency?: string | null
  timezone?: string | null
  is_bookable?: boolean
  is_published?: boolean
}

export interface AvailabilityBlock {
  id: number
  day_of_week: number
  start_time: string
  end_time: string
  is_active: boolean
}

export interface Blackout {
  id: number
  date_from: string
  date_to: string
  reason: string | null
}

export interface Booking {
  id: number
  customer_name: string
  customer_email: string
  customer_phone: string | null
  starts_at: string | null
  duration: number
  amount_paid: number
  currency: string | null
  is_free: boolean
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'pending_payment'
  meeting_url: string | null
  customer_note: string | null
  staff_note: string | null
}

export interface PublicBookingProfile {
  id: number
  slug: string
  display_name: string
  avatar: string | null
  title: string | null
  bio: string | null
  expertise: string[]
  main_price: number
  currency: string | null
  session_duration: number
  timezone: string
  rating_avg: number | null
  ratings_count: number
}

export interface SlotsResponse {
  data: Record<string, Array<{ starts_at: string; ends_at: string }>>
  timezone: string
}

export interface BookInput {
  starts_at: string
  customer_name: string
  customer_email: string
  customer_phone?: string | null
  customer_note?: string | null
  payment_method?: 'stripe' | null
}

export const useBookingRepository = () => {
  const api = useApi()

  // ── Staff (self) ─────────────────────────────────────────────────────

  const getProfile = () =>
    api.get<ApiEnvelope<BookingProfile | null>>('/api/staff/booking/profile')

  const createProfile = (body: BookingProfileInput) =>
    api.post<ApiEnvelope<BookingProfile>>('/api/staff/booking/profile', { body })

  const updateProfile = (body: BookingProfileInput) =>
    api.patch<ApiEnvelope<BookingProfile>>('/api/staff/booking/profile', { body })

  const deleteProfile = () =>
    api.del('/api/staff/booking/profile')

  const toggleBookable = () =>
    api.post<ApiEnvelope<{ is_bookable: boolean }>>('/api/staff/booking/profile/toggle-bookable')

  const togglePublished = () =>
    api.post<ApiEnvelope<{ is_published: boolean }>>('/api/staff/booking/profile/toggle-published')

  const listAvailability = () =>
    api.get<ApiEnvelope<AvailabilityBlock[]>>('/api/staff/booking/availability')

  const createAvailability = (body: { day_of_week: number; start_time: string; end_time: string }) =>
    api.post<ApiEnvelope<AvailabilityBlock>>('/api/staff/booking/availability', { body })

  const updateAvailability = (id: number, body: Partial<Pick<AvailabilityBlock, 'day_of_week' | 'start_time' | 'end_time' | 'is_active'>>) =>
    api.patch<ApiEnvelope<AvailabilityBlock>>(`/api/staff/booking/availability/${id}`, { body })

  const deleteAvailability = (id: number) =>
    api.del(`/api/staff/booking/availability/${id}`)

  const listBlackouts = () =>
    api.get<ApiEnvelope<Blackout[]>>('/api/staff/booking/blackouts')

  const createBlackout = (body: { date_from: string; date_to: string; reason?: string | null }) =>
    api.post<ApiEnvelope<Blackout>>('/api/staff/booking/blackouts', { body })

  const deleteBlackout = (id: number) =>
    api.del(`/api/staff/booking/blackouts/${id}`)

  const listSessions = () =>
    api.get<{ data: Booking[]; meta?: { total: number } }>('/api/staff/booking/sessions')

  const updateSession = (id: number, body: Partial<Pick<Booking, 'status' | 'meeting_url' | 'staff_note'>>) =>
    api.patch<ApiEnvelope<Booking>>(`/api/staff/booking/sessions/${id}`, { body })

  // ── Public ───────────────────────────────────────────────────────────

  const publicListStaff = () =>
    api.get<ApiEnvelope<PublicBookingProfile[]>>('/api/public/booking/staff')

  const publicShowStaff = (slug: string) =>
    api.get<ApiEnvelope<PublicBookingProfile>>(`/api/public/booking/staff/${slug}`)

  const publicSlots = (slug: string, from: string, to: string) =>
    api.get<SlotsResponse>(`/api/public/booking/staff/${slug}/slots`, { query: { from, to } })

  const publicBook = (slug: string, body: BookInput) =>
    api.post<{
      data?: { id: number; starts_at: string; duration: number; status: string }
      status?: string
      booking_id?: number
      checkout_url?: string
      payment?: Record<string, unknown>
    }>(`/api/public/booking/staff/${slug}/book`, { body })

  const publicUploadReceipt = (bookingId: number, customerEmail: string, file: File) => {
    const form = new FormData()
    form.append('customer_email', customerEmail)
    form.append('file', file)
    return api.post<{ data: { uploaded: boolean; payment_id: number } }, FormData>(
      `/api/public/bookings/${bookingId}/receipt`,
      { body: form },
    )
  }

  const publicRate = (token: string, bookingId: number, body: { rating: number; review?: string | null }) =>
    api.post<{ data: { id: number; rating: number; review: string | null } }>(
      `/api/public/bookings/${bookingId}/rate?signature=${token}`,
      { body },
    )

  return {
    getProfile,
    createProfile,
    updateProfile,
    deleteProfile,
    toggleBookable,
    togglePublished,
    listAvailability,
    createAvailability,
    updateAvailability,
    deleteAvailability,
    listBlackouts,
    createBlackout,
    deleteBlackout,
    listSessions,
    updateSession,
    publicListStaff,
    publicShowStaff,
    publicSlots,
    publicBook,
    publicUploadReceipt,
    publicRate,
  }
}
